import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { IJoinWrite } from '../../../LogisticsPage/orderHandoff';
import { lineFor } from '../../../LogisticsPage/lines';
import { formatDay } from '../../../LogisticsPage/logistics.format';
import type { ICheckoutPlan } from '../../stock/checkoutPlan';
import { etaAfterTaskStart, type JoinRefusal } from '../../stock/joinOrder';
import { fireThenPersist } from '../../stock/persistAfterWrite';
import { joinErrorKey, useJoinWrites, type IJoinFailure } from '../../stock/useJoinWrites';
import { BackToMaterialsButton } from './BackToMaterialsButton';
import { StockOrderCard } from './StockOrderCard';
import tabStyles from './TaskPanelStockTab.module.css';
import styles from './StockCart.module.css';

interface IStockCheckoutReviewProps {
  siteId: string;
  plan: ICheckoutPlan;
  /** The plan's joins as ready writes, in the same order (`joinWritesFor`). */
  writes: IJoinWrite[];
  /** The task's own start (YYYY-MM-DD) — an ETA past it turns an add amber. */
  taskStart: string;
  /** Runs right after the adds are fired: the drawer persists its drafted
   *  claims through the immediate-write channel, so an order never points at
   *  a booking that was never saved. */
  onBeforeJoin: () => void;
  /** Back to the list — also where the review goes once its adds landed. */
  onBack: () => void;
  /** Opens the order details (the delivery dialog) for the new order. */
  onContinue: () => void;
  /** Reopens a material's own form — the page no longer lists what is still
   *  to be ordered, so the review is where it is reached from. */
  onEdit: (claimId: string) => void;
  /** Drops the material from the task. */
  onRemove: (claimId: string) => void;
}

const K = 'scheduler.panel.stock.cart';

/** What each refusal asks the user to change. */
const REFUSAL_KEY: Record<JoinRefusal, string> = {
  unit: `${K}.blockedUnit`,
  noUnit: `${K}.blockedNoUnit`,
  freeText: `${K}.blockedFreeText`,
};

function Group({ label, count, children }: { label: string; count: number; children: ReactNode }) {
  return (
    <section className={styles.group}>
      <div className={styles.groupHead}>
        <span className={styles.groupLabel}>{label}</span>
        <span>{count}</span>
      </div>
      {children}
    </section>
  );
}

/**
 * The Stock tab's checkout (contract v14): the list sorted once, taking the
 * tab body's place like the element list does. Each material sits in exactly
 * one group — never a choice between orders, since at most one open order
 * can carry a material.
 *
 * With something new to order, the primary opens the order details and
 * Create does the adds too. With only adds, it performs them right here and
 * returns to the list; a refused one stays, named, for another try.
 */
export function StockCheckoutReview({ siteId, plan, writes, taskStart, onBeforeJoin, onBack, onContinue, onEdit, onRemove }: IStockCheckoutReviewProps) {
  const { t, i18n } = useTranslation();
  const joins = useJoinWrites(siteId);
  const [failures, setFailures] = useState<IJoinFailure[]>([]);
  const day = (isoDate: string) => formatDay(isoDate, i18n.language);
  const refs = [...new Set(plan.joins.map((j) => j.order.ref))];
  const creates = plan.newLines.length > 0;

  const addAll = () =>
    fireThenPersist(() => {
      void joins.run(writes).then((failed) => {
        if (failed.length === 0) onBack();
        else setFailures(failed);
      });
    }, onBeforeJoin);

  const acts = (claimId: string) => (
    <span className={styles.lineActs}>
      <button type="button" className={styles.act} onClick={() => onEdit(claimId)}>
        {t(`${K}.edit`)}
      </button>
      <button type="button" className={styles.act} onClick={() => onRemove(claimId)}>
        {t(`${K}.remove`)}
      </button>
    </span>
  );

  const primaryLabel = joins.pending
    ? t('scheduler.panel.stock.join.adding')
    : creates || refs.length === 0
      ? t(`${K}.continue`)
      : refs.length === 1
        ? t(`${K}.addTo`, { ref: refs[0] })
        : t(`${K}.addToMany`, { count: refs.length });

  return (
    <div className={tabStyles.body}>
      <BackToMaterialsButton className={tabStyles.backToCad} onClick={onBack} />

      {plan.joins.length > 0 && (
        <Group label={t(`${K}.groupJoin`)} count={plan.joins.length}>
          {plan.joins.map(({ claim, order, amount }) => {
            const late = etaAfterTaskStart(order, taskStart);
            const failure = failures.find((f) => f.claimId === claim.id);
            return (
              <div key={claim.id} className={styles.box} data-tone={late === null ? 'info' : 'warn'}>
                <div className={styles.line}>
                  <span>{claim.materialName}</span>
                  <span className={styles.lineQty}>{`+${amount} ${order.line.unit}`}</span>
                  {acts(claim.id)}
                </div>
                <StockOrderCard siteId={siteId} order={order} amount={amount} />
                {late !== null && (
                  <p className={styles.late}>
                    {t('scheduler.panel.stock.join.lineLate', { ref: order.ref, eta: day(late), start: day(taskStart) })}
                  </p>
                )}
                {failure && <p className={tabStyles.joinError}>{t(joinErrorKey(failure.error))}</p>}
              </div>
            );
          })}
        </Group>
      )}

      {plan.blocked.length > 0 && (
        <Group label={t(`${K}.groupBlocked`)} count={plan.blocked.length}>
          {plan.blocked.map(({ claim, order, reason }) => (
            <div key={claim.id} className={styles.box} data-tone="warn">
              <div className={styles.line}>
                <span>{claim.materialName}</span>
                {acts(claim.id)}
              </div>
              <p className={styles.boxNote}>
                {/* The order was found by this material, so it carries the line. */}
                {t(REFUSAL_KEY[reason], { ref: order.ref, unit: lineFor(order, claim.materialName)!.unit })}
              </p>
            </div>
          ))}
        </Group>
      )}

      {creates && (
        <Group label={t(`${K}.groupNew`)} count={plan.newLines.length}>
          <div className={styles.box}>
            {plan.newLines.map(({ claim, need }) => (
              <div key={claim.id} className={styles.line}>
                <span>{claim.materialName}</span>
                <span className={styles.lineQty}>{`${need} ${claim.unit}`}</span>
                {acts(claim.id)}
              </div>
            ))}
            <p className={styles.boxNote}>{t(`${K}.newNote`)}</p>
          </div>
        </Group>
      )}

      <div className={styles.reviewFoot}>
        <button type="button" className={styles.ghost} onClick={onBack}>
          {t(`${K}.back`)}
        </button>
        <button
          type="button"
          className={styles.primary}
          // Nothing it can do while only "needs a change" is left.
          disabled={joins.pending || (!creates && refs.length === 0)}
          onClick={creates ? onContinue : addAll}
        >
          {primaryLabel}
        </button>
      </div>
    </div>
  );
}
